import { ChangeEvent, FC, KeyboardEvent } from 'react';
import { Box, Stack, Theme } from '@mui/material';
import { TextareaAutosize } from '@mui/base/TextareaAutosize';
import FormControl from '../form-control';
import { FormProps } from '../helpers';
import { createUseStyles } from 'react-jss';
import classNames from 'classnames';

interface Props extends FormProps {
  onChange: (value: string, event: ChangeEvent<HTMLTextAreaElement>) => void;
  onEnter?: (value: string) => void;
  maxRows?: number;
  minRows?: number;
  maxChars?: number;
  value: string;
}

const COUNT_ERROR_THRESHOLD = 0.95;

const useStyles = createUseStyles((theme: Theme) => ({
  textarea: {
    padding: 10,
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize,
    borderRadius: 4,
    resize: 'none',
  },
  charCount: {
    alignSelf: 'flex-end',
    fontSize: theme.typography.caption.fontSize,
    color: theme.palette.darkGray,
    '&.error': {
      color: theme.palette.error.dark,
    },
  },
}));

const TextArea: FC<Props> = ({
  autoFocus,
  label,
  error,
  name,
  onChange,
  onEnter,
  value,
  maxRows,
  minRows,
  maxChars,
}) => {
  const styles = useStyles();
  const charCount = value.length;
  const hasMaxChars = maxChars != null && maxChars > 0;
  const countPercent = hasMaxChars ? charCount / maxChars : 0;
  const charCountClassName = classNames(styles.charCount, {
    error: countPercent >= COUNT_ERROR_THRESHOLD,
  });

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = String(event.target.value);
    const truncated = hasMaxChars ? newValue.slice(0, maxChars) : newValue;
    onChange(truncated, event);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter') {
      onEnter?.(value);
    }
  };

  return (
    <FormControl error={error} name={name}>
      <Stack gap={0.5}>
        <TextareaAutosize
          onKeyDown={handleKeyDown}
          maxRows={maxRows}
          minRows={minRows}
          value={value}
          onChange={handleChange}
          autoFocus={autoFocus}
          className={styles.textarea}
          placeholder={label as string}
        />
        {hasMaxChars && (
          <Box className={charCountClassName}>
            {charCount}/{maxChars}
          </Box>
        )}
      </Stack>
    </FormControl>
  );
};

export default TextArea;
